import React, { useState, useEffect, useContext } from "react";
import { Card, Col, Form, Button, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "../../../redux/slices/preferences";
import NotyfContext from "../../../contexts/NotyfContext";
import * as Yup from "yup";
import { Formik } from "formik";
import Loader from "../../../components/Loader";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../../hooks/useWindowSize";
import useDeviceType from "../../../hooks/useDeviceType";

const Notifications = ({ activeTab }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const notificationPreferences = useSelector(
    (state) => state.preferences.notificationPreferences
  );
  const notyf = useContext(NotyfContext);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      setIsLoading(true);
      await dispatch(getNotificationPreferences())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        });
    };

    if (activeUser.id && activeTab === "#Notifications") {
      fetchNotificationPreferences();
    }
  }, [activeUser, activeTab, dispatch, notyf]);

  const initialValues = {
    email_enabled: notificationPreferences.emailEnabled,
    sms_enabled: notificationPreferences.smsEnabled,
    weekly_analytics: notificationPreferences.weeklyAnalytics,
    new_alerts: notificationPreferences.newAlerts,
  };

  const validation = Yup.object().shape({
    email_enabled: Yup.boolean(),
    sms_enabled: Yup.boolean(),
    weekly_analytics: Yup.boolean(),
    new_alerts: Yup.boolean(),
  });

  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">
          {t("settings.notificationSettings.title")}
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading && <Loader />}
        {!isLoading && (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues, setSubmitting }) => {
              await dispatch(updateNotificationPreferences({ data: values }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Successfully updated notification preferences",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  {isDesktop && (
                    <Form.Group className="mb-3">
                      <Form.Label column sm={2} className="text-sm-right">
                        {t(
                          "settings.notificationSettings.receiveNotifications"
                        )}
                        :
                      </Form.Label>
                      <Form.Check
                        type="checkbox"
                        name="smsEnabled"
                        checked={values.sms_enabled}
                        onChange={(e) =>
                          setFieldValue("sms_enabled", e.target.checked)
                        }
                        onBlur={handleBlur}
                        label="SMS"
                        className="mb-2"
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#fff",
                          border: "none",
                        }}
                      />
                      <Form.Check
                        type="checkbox"
                        name="email_enabled"
                        checked={values.email_enabled}
                        onChange={(e) =>
                          setFieldValue("email_enabled", e.target.checked)
                        }
                        onBlur={handleBlur}
                        label="E-mail"
                        className="mb-2"
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#fff",
                          border: "none",
                        }}
                      />
                    </Form.Group>
                  )}
                  {!isDesktop && (
                    <div className="mb-3">
                      <Form.Label>
                        {t(
                          "settings.notificationSettings.receiveNotifications"
                        )}
                        :
                      </Form.Label>
                      <Form.Group className="mb-3 d-flex justify-content-between">
                        <Form.Label>SMS</Form.Label>
                        <Form.Check
                          type="checkbox"
                          name="smsEnabled"
                          checked={values.sms_enabled}
                          onChange={(e) =>
                            setFieldValue("sms_enabled", e.target.checked)
                          }
                          onBlur={handleBlur}
                          className="mb-2 notification-checkbox"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#fff",
                            border: "none",
                          }}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3 d-flex justify-content-between">
                        <Form.Label>Email</Form.Label>
                        <Form.Check
                          type="checkbox"
                          name="email_enabled"
                          checked={values.email_enabled}
                          onChange={(e) =>
                            setFieldValue("email_enabled", e.target.checked)
                          }
                          onBlur={handleBlur}
                          className="mb-2 notification-checkbox"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#fff",
                            border: "none",
                          }}
                        />
                      </Form.Group>
                    </div>
                  )}
                  {isDesktop && (
                    <Form.Group as={Row} className="mb-3 ml-3">
                      <Form.Label
                        htmlFor="#weeklyAnalytics"
                        className="font-weight-bold"
                      >
                        {t("settings.notificationSettings.weeklyAnalytics")}
                      </Form.Label>
                      <Col sm={10}>
                        <Form.Check
                          type="switch"
                          name="weekly_analytics"
                          checked={values.weekly_analytics}
                          onChange={(e) =>
                            setFieldValue("weekly_analytics", e.target.checked)
                          }
                          onBlur={handleBlur}
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#fff",
                            border: "none",
                          }}
                        />
                      </Col>
                    </Form.Group>
                  )}
                  {!isDesktop && (
                    <Form.Group className="mb-3 d-flex justify-content-between">
                      <Form.Label
                        htmlFor="#weeklyAnalytics"
                        className="font-weight-bold"
                      >
                        {t("settings.notificationSettings.weeklyAnalytics")}
                      </Form.Label>
                      <Form.Check
                        type="switch"
                        name="weekly_analytics"
                        checked={values.weekly_analytics}
                        onChange={(e) =>
                          setFieldValue("weekly_analytics", e.target.checked)
                        }
                        onBlur={handleBlur}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#fff",
                          border: "none",
                        }}
                      />
                    </Form.Group>
                  )}

                  {isDesktop && (
                    <Form.Group className="mb-3">
                      <Form.Label
                        htmlFor="#notifyAlerts"
                        className="font-weight-bold"
                      >
                        {t(
                          "settings.notificationSettings.notifyAboutNewAlerts"
                        )}
                      </Form.Label>
                      <Form.Check
                        type="switch"
                        name="new_alerts"
                        checked={values.new_alerts}
                        onChange={(e) =>
                          setFieldValue("new_alerts", e.target.checked)
                        }
                        onBlur={handleBlur}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#fff",
                          border: "none",
                        }}
                      />
                    </Form.Group>
                  )}
                  {!isDesktop && (
                    <Form.Group className="mb-3 d-flex justify-content-between">
                      <Form.Label
                        htmlFor="#notifyAlerts"
                        className="font-weight-bold"
                      >
                        {t(
                          "settings.notificationSettings.notifyAboutNewAlerts"
                        )}
                      </Form.Label>
                      <Form.Check
                        type="switch"
                        name="new_alerts"
                        checked={values.new_alerts}
                        onChange={(e) =>
                          setFieldValue("new_alerts", e.target.checked)
                        }
                        onBlur={handleBlur}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#fff",
                          border: "none",
                        }}
                      />
                    </Form.Group>
                  )}

                  {isDesktop && (
                    <Row className="mb-3">
                      <Col md="auto ms-auto">
                        <Button
                          type="submit"
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#fff",
                            border: "none",
                          }}
                        >
                          {t("settings.notificationSettings.submit")}
                        </Button>
                      </Col>
                    </Row>
                  )}
                  {!isDesktop && (
                    <div className="mt-3 d-flex justify-content-center">
                      <Button
                        type="submit"
                        variant="primary"
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#fff",
                          border: "none",
                          width: "100%",
                          padding: "10px",
                        }}
                      >
                        {t("settings.notificationSettings.submit")}
                      </Button>
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </Card.Body>
    </Card>
  );
};

export default Notifications;
